import { getCountry, updateCountry } from '../country';
import { getObj } from '../localStorage';
import { CustomError, logError } from '../sentry';

const getCountrySelectorElement = () => document.getElementById('countrySelector');

const countryNames = {
  AT: 'Österreich',
  BE: 'België/Belgique',
  CY: 'Κύπρος',
  CZ: 'Česká republika',
  DE: 'Deutschland',
  DK: 'Danmark',
  EE: 'Eesti',
  ES: 'España',
  EU: 'European Union',
  FI: 'Suomi',
  FR: 'France',
  GB: 'United Kingdom',
  GR: 'Ελλάδα',
  HR: 'Hrvatska',
  HU: 'Magyarország',
  IE: 'Ireland',
  IT: 'Italia',
  LT: 'Lietuva',
  LU: 'Luxembourg',
  LV: 'Latvija',
  MT: 'Malta',
  NL: 'Nederland',
  PL: 'Polska',
  PT: 'Portugal',
  SE: 'Sverige',
  SI: 'Slovenija',
  SK: 'Slovensko',
  US: 'United States',
};

const getCountryNameFallback = (countryCode) =>
  countryNames[countryCode] || new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode);

const showNotAvailableMessage = () => {
  const modal = document.getElementById('countryModal');
  const modalContent = modal.querySelector('.modal-content');

  modalContent.innerHTML = `
    <span class="close">&times;</span>
    <h1 class="funneltitle">Other Country</h1>
    <p class="description">Interested in having klikkie in your country? Let us know!</p>
    <div style="margin: 20px;">
      <a href="mailto:hello@klikkie.com?subject=Request for klikkie in my country&body=Hi klikkie team,%0D%0A%0D%0AI would like to request klikkie services in my country.%0D%0A%0D%0ACountry: [Please specify]%0D%0A%0D%0AThank you!" 
         style="-webkit-text-size-adjust: 100%; --light-sea-green: #18b2bb; --seashell: transparent; --white: white; --text-gray: #333; --d7f8ed: var(--light-sea-green); --deep-pink: #f0a; --white-smoke-2: #f5f4f2; --honeydew: #d5f2d4; --pink: #ffcee9; --salmon: #ff8172; --pale-turquoise-2: #c4f8fb; --white-smoke: #fde7f0; --gray: #555; box-sizing: border-box; color: #fff; cursor: pointer; border: 0; text-decoration: none; display: inline-block; z-index: 1; background-color: var(--text-gray); text-align: center; letter-spacing: 1px; text-transform: uppercase; border-radius: 30px; align-self: auto; width: 100%; max-width: 360px; margin-top: 10px; padding: 0 24px; font-family: brandon-grotesque,sans-serif; font-size: 14px; font-weight: 500; line-height: 48px; position: relative;">
        REQUEST MY COUNTRY
      </a>
    </div>
    <p style="font-size: 14px; color: #666; margin-top: 20px;">
      We'll get back to you as soon as possible to discuss availability in your region.
    </p>
  `;

  // Re-add close functionality
  const closeButton = modalContent.querySelector('.close');
  Object.assign(closeButton.style, {
    color: '#aaa',
    float: 'right',
    fontSize: '28px',
    fontWeight: 'bold',
    cursor: 'pointer',
  });

  closeButton.onmouseover = () => {
    closeButton.style.color = 'black';
  };
  closeButton.onmouseout = () => {
    closeButton.style.color = '#aaa';
  };

  const closeModal = () => {
    modal.style.display = 'none';
    // KL-16009: an order waiting for a pick learns that none is coming.
    document.dispatchEvent(new CustomEvent('klikkie:country-unavailable'));
  };
  closeButton.addEventListener('click', closeModal);
};

// Shared dropdown arrow SVG for consistent styling
const getDropdownArrowSvg = () =>
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>'
  );

// House icon SVG (for country/shipping location)
const houseIconSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -1px; flex-shrink: 0;">
  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
  <polyline points="9 22 9 12 15 12 15 22"></polyline>
</svg>`;

// Globe icon SVG (for language)
const globeIconSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -1px; flex-shrink: 0;">
  <circle cx="12" cy="12" r="10"></circle>
  <line x1="2" y1="12" x2="22" y2="12"></line>
  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
</svg>`;

// Shared select styles for footer selectors
const footerSelectStyle = `
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: transparent;
  font-size: 12px;
  color: #999;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-repeat: no-repeat;
  background-position: right 8px center;
  padding-right: 28px;
`;

const generateCountrySelector = (styled = false) => {
  const selectId = 'countrySelect';
  const optionsArr = Object.keys(countryNames)
    .filter((country) => country !== 'EU')
    .sort((a, b) =>
      getCountryNameFallback(a).localeCompare(getCountryNameFallback(b), 'en', {
        sensitivity: 'base',
      })
    )
    .map((country) => `<option value="${country}">${getCountryNameFallback(country)}</option>`);

  // Add "Not Available" option
  optionsArr.push(`<option value="NOT_AVAILABLE">------------</option>`);
  optionsArr.push(`<option value="NOT_AVAILABLE">Other Country</option>`);

  const options = optionsArr.join('');

  // Use styled version for modal, footer version for footer
  if (styled) {
    const modalSelectStyle = `
      font-size: 16px;
      padding: 5px;
      width: 50%;
      border-radius: 4px;
      margin-bottom: 15px;
      font-weight: 600;
      letter-spacing: 1px;
    `;
    return `<select name="${selectId}" id="${selectId}" style="${modalSelectStyle}"><option value="">Select your country</option>${options}</select>`;
  }

  // Footer version with label and consistent styling
  const dropdownArrowSvg = getDropdownArrowSvg();
  return `
    <div style="display: flex; align-items: center; gap: 6px; color: #999;">
      ${houseIconSvg}
      <select name="${selectId}" id="${selectId}" style="${footerSelectStyle} background-image: url('data:image/svg+xml,${dropdownArrowSvg}');">
        <option value="">Select country</option>
        ${options}
      </select>
    </div>
  `;
};

const handleCountryChange = async (e) => {
  try {
    const countryCode = e.target.value;

    // Handle "Not Available" option
    if (countryCode === 'NOT_AVAILABLE') {
      const modal = document.getElementById('countryModal');
      if (modal) {
        // Modal version - show message in modal
        showNotAvailableMessage();
      } else {
        // Injected version - redirect to contact page or show alert
        window.open(
          'mailto:hello@klikkie.com?subject=Request for klikkie in my country&body=Hi klikkie team,%0D%0A%0D%0AI would like to request klikkie services in my country.%0D%0A%0D%0ACountry: [Please specify]%0D%0A%0D%0AThank you!',
          '_blank'
        );
      }
      return;
    }

    // Handle empty selection
    if (!countryCode) {
      return;
    }

    console.log(`Country changed to "${countryCode}", updating user, order, and funnel...`);

    // Stores the pick and pushes it to the profile. The local write and the
    // `klikkie:country` dispatch inside it are synchronous, so the UI has
    // already repainted by the time the returned profile PUT settles.
    const countrySynced = updateCountry(countryCode);

    // Dispatch custom event for country change FIRST (so pages can refresh data)
    // This must happen before API calls that might fail
    window.dispatchEvent(new CustomEvent('klikkie:countryChanged', { detail: { countryCode } }));

    await countrySynced;

    // The profile is updateCountry's job now; the order is still ours. Might
    // fail, but the UI is already updated and the pick is already stored.
    if (getObj('user').userId && getObj('order').id) {
      try {
        const { updateOrder } = await import(/* webpackChunkName: "order" */ '../order');
        await updateOrder(undefined, countryCode);
      } catch (apiError) {
        // Log but don't throw - UI already updated, backend sync can be retried
        console.warn('Failed to sync country change to backend:', apiError);
      }
    }

    // Close the modal after country selection
    const modal = document.getElementById('countryModal');
    if (modal) {
      modal.style.display = 'none';
    }
    // Note: No page reload - pages should listen for klikkie:countryChanged event
  } catch (error) {
    logError(error, { countryCode: e.target.value });
    throw new CustomError('Failed to handle country change', 'warning');
  }
};

export const initializeCountrySelector = async () => {
  try {
    getCountrySelectorElement().innerHTML = generateCountrySelector();

    const countrySelected = await getCountry();
    const countrySelectElement = document.getElementById('countrySelect');
    countrySelectElement.value = countrySelected;

    countrySelectElement.addEventListener('change', handleCountryChange);
  } catch (error) {
    logError(error);
    throw new CustomError('Failed to initialize country selector', 'warning');
  }
};

// `required`: an order is waiting for this pick (KL-16009), so there is no way
// to close the picker. One picker per page: the page's own country check and an
// order that needs a country can both ask for it, in either order.
export const renderCountryModal = async ({ required = false } = {}) => {
  try {
    const openModal = document.getElementById('countryModal');
    // Still holding the dropdown: show it again. After "Other Country" it holds
    // only the request message, so it is built anew.
    if (openModal?.querySelector('select')) {
      if (required) openModal.querySelector('.close')?.remove();
      openModal.style.display = 'block';
      return;
    }
    openModal?.remove();

    // Code to create and display the modal
    const modalElement = document.createElement('div');
    modalElement.id = 'countryModal';
    // modalElement.style.display = 'none'; // initially hidden
    modalElement.innerHTML = `
      <div class="modal-content">
        ${required ? '' : '<span class="close">&times;</span>'}
        <h1 class="funneltitle">Where are you from?</h1>
        <p class="description">We need to know where you're joining us from. <br/><br/>Simply select your country below.</p>
        <div id="countrySelector" class="countryModalSelector"></div>
      </div>
    `;

    // Append modal to body
    document.body.appendChild(modalElement);

    // Styling for the modal
    Object.assign(modalElement.style, {
      position: 'fixed',
      zIndex: '999999',
      left: '0',
      top: '0',
      width: '100%',
      height: '100%',
      overflow: 'auto',
      backgroundColor: 'rgba(0,0,0,0.4)',
    });

    // Styling for the modal content
    const modalContent = modalElement.querySelector('.modal-content');
    Object.assign(modalContent.style, {
      backgroundColor: '#fefefe',
      margin: '15% auto',
      padding: '20px',
      border: '1px solid #888',
      width: '80%',
      maxWidth: '500px',
      borderRadius: '10px',
      color: '#333',
      fontFamily: 'Lato,sans-serif',
      fontSize: '14px',
      textAlign: 'center',
    });

    // Styling for the description
    const description = modalContent.querySelector('.description');
    Object.assign(description.style, {
      webkitTextSizeAdjust: '100%',
      '--light-sea-green': '#18b2bb',
      '--seashell': 'transparent',
      '--white': 'white',
      '--text-gray': '#333',
      '--d7f8ed': 'var(--light-sea-green)',
      '--deep-pink': '#f0a',
      '--white-smoke-2': '#f5f4f2',
      '--honeydew': '#d5f2d4',
      '--pink': '#ffcee9',
      '--salmon': '#ff8172',
      '--pale-turquoise-2': '#c4f8fb',
      '--white-smoke': '#fde7f0',
      '--gray': '#555',
      color: '#333',
      fontFamily: 'Lato,sans-serif',
      boxSizing: 'border-box',
      textAlign: 'center',
      marginTop: '20px',
      marginBottom: '10px',
      fontSize: '1.1em',
      lineHeight: '1.6em',
    });

    // Styling for the close button
    const closeButton = modalContent.querySelector('.close');
    if (closeButton) {
      Object.assign(closeButton.style, {
        color: '#aaa',
        float: 'right',
        fontSize: '28px',
        fontWeight: 'bold',
        cursor: 'pointer',
      });

      closeButton.onmouseover = () => {
        closeButton.style.color = 'black';
      };
      closeButton.onmouseout = () => {
        closeButton.style.color = '#aaa';
      };

      // Close functionality for modal
      closeButton.addEventListener('click', () => {
        modalElement.style.display = 'none';
      });
    }

    // Add the country selector. The modal's own one: subscribe and gift pages
    // carry a #countrySelector of their own, earlier in the document.
    const modalSelector = modalElement.querySelector('#countrySelector');
    modalSelector.innerHTML = generateCountrySelector(true);
    modalSelector.addEventListener('change', handleCountryChange);

    // Display the modal
    modalElement.style.display = 'block';
  } catch (error) {
    logError(error);
    throw new CustomError('Failed to render country modal', 'warning');
  }
};

/**
 * KL-16009: wait for the visitor to pick a country, with a picker that stays
 * until they do. Resolves with the country once it is stored: a pick goes
 * through updateCountry(), which dispatches `klikkie:country`, as does any other
 * write (a later lookup), after which the question is moot and the picker
 * closes. Rejects when the visitor closes the "Other Country" message (we sell
 * nothing where they are) or the picker cannot be shown, so the caller fails
 * loudly instead of waiting forever.
 */
export const requireCountryPick = () =>
  new Promise((resolve, reject) => {
    const listening = new AbortController();
    const { signal } = listening;
    document.addEventListener(
      'klikkie:country',
      (event) => {
        const country = event?.detail?.country;
        if (!country) return;
        listening.abort();
        const modal = document.getElementById('countryModal');
        if (modal) modal.style.display = 'none';
        resolve(country);
      },
      { signal }
    );
    document.addEventListener(
      'klikkie:country-unavailable',
      () => {
        listening.abort();
        reject(new CustomError('No country we sell to was picked', 'warning'));
      },
      { signal }
    );
    renderCountryModal({ required: true }).catch((error) => {
      listening.abort();
      reject(error);
    });
  });

/**
 * Available languages and their labels
 */
const languageNames = {
  en: 'English',
  nl: 'Nederlands',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
};

/** Language → subdomain mapping */
const langDomains = {
  en: 'www.klikkie.com',
  nl: 'nl.klikkie.com',
  de: 'de.klikkie.com',
  fr: 'fr.klikkie.com',
  es: 'es.klikkie.com',
};

/**
 * Get the language selector element
 */
const getLanguageSelectorElement = () => document.getElementById('languageSelector');

/**
 * Generate HTML for the language selector
 */
const generateLanguageSelector = (currentLang) => {
  const options = Object.entries(languageNames)
    .map(
      ([code, name]) =>
        `<option value="${code}" ${code === currentLang ? 'selected' : ''}>${name}</option>`
    )
    .join('');

  const dropdownArrowSvg = getDropdownArrowSvg();

  return `
    <div style="display: flex; align-items: center; gap: 6px; color: #999;">
      ${globeIconSvg}
      <select id="languageSelect" style="${footerSelectStyle} background-image: url('data:image/svg+xml,${dropdownArrowSvg}');">
        ${options}
      </select>
    </div>
  `;
};

/**
 * Handle language change — redirect to the same path on the target subdomain
 */
const handleLanguageChange = (event) => {
  const selectedLanguage = event.target.value;
  const targetDomain = langDomains[selectedLanguage];
  if (!targetDomain) return;

  const currentHost = window.location.hostname;
  const targetHost = targetDomain;

  // Dev environment: adjust domain (e.g., dev.de.klikkie.com)
  const isDev = currentHost.startsWith('dev.') || currentHost.includes('klikkie-website-dev');
  if (isDev) {
    const devPrefix = selectedLanguage === 'en' ? 'dev.' : `dev.${selectedLanguage}.`;
    const devDomain = devPrefix + 'klikkie.com';
    window.location.href = `${window.location.protocol}//${devDomain}${window.location.pathname}${window.location.search}`;
    return;
  }

  if (currentHost !== targetHost) {
    window.location.href = `${window.location.protocol}//${targetHost}${window.location.pathname}${window.location.search}`;
  }
};

/**
 * Initialize the language selector in the footer.
 * Language is determined by the subdomain — no external dependency needed.
 */
export const initializeLanguageSelector = () => {
  const container = getLanguageSelectorElement();
  if (!container) return;

  const { getUserLanguage } = require('../language');
  const currentLang = getUserLanguage();

  container.innerHTML = generateLanguageSelector(currentLang);

  const selectElement = document.getElementById('languageSelect');
  if (selectElement) {
    selectElement.addEventListener('change', handleLanguageChange);
  }
};
